import { useEffect, useMemo, useRef, useState } from 'react'
import { ChallengeService, TaskService } from '@duck/api-definitions'
import { Button } from '@duck/ui'
import { ChevronLeft, Feather, Flame, Shuffle, X } from 'lucide-react'
import { useTar } from '@/modules'
import { useStartChallenge } from '@/hooks/useStartChallenge'
import { WeaponIcon } from '@components/WeaponIcon'
import { MilestoneProgress } from './MilestoneProgress'
import { ChoiceCardGroup, type ChoiceOption } from './ChoiceCardGroup'
import { ProficiencyPicker } from './ProficiencyPicker'
import { HelpMePickResult } from './HelpMePickResult'
import { HelpMePickMatches } from './HelpMePickMatches'
import { filterMatches, type HelpMePickAnswers, type Intensity, type ToolChoice } from './helpMePick.logic'
import { pickUnserved } from './helpMePick.picker'
import { readServed, writeServed } from './servedStore'
import { trackHelpMePick } from '@/modules/challengesAnalytics'

const STEP_COUNT = 4 // Q1, Q2, Q3, Finish

const emptyAnswers = (): HelpMePickAnswers => ({ intensity: null, tool: null, proficiencies: [] })

const challengeId = (c: ChallengeService.AvailableChallenge): string => c.challengeId ?? c.taskId

type ResultSource = 'autoSingle' | 'pickForMe' | 'pickAnother' | 'matchesList'

export const HelpMePickWizard = ({
  challenges,
  open,
  onClose,
}: {
  challenges: ChallengeService.AvailableChallenge[]
  open: boolean
  onClose: () => void
}) => {
  const { t } = useTar()
  const startChallenge = useStartChallenge()

  const [step, setStep] = useState(0)
  const [screen, setScreen] = useState<'steps' | 'matches' | 'result'>('steps')
  const [answers, setAnswers] = useState<HelpMePickAnswers>(emptyAnswers)
  const [result, setResult] = useState<ChallengeService.AvailableChallenge | null>(null)
  const [cameFromMatches, setCameFromMatches] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Latest screen/step for the close event, read live from a stable ref so the
  // Escape handler reports where the user actually abandoned the wizard.
  const progressRef = useRef({ screen, step })
  progressRef.current = { screen, step }

  // Reset everything each time the wizard opens; drive the fade-in.
  useEffect(() => {
    if (open) {
      setStep(0)
      setScreen('steps')
      setAnswers(emptyAnswers())
      setResult(null)
      setCameFromMatches(false)
      trackHelpMePick('helpMePick:open', { totalChallenges: challenges.length })
      const id = requestAnimationFrame(() => setMounted(true))
      return () => cancelAnimationFrame(id)
    }
    setMounted(false)
  }, [open, challenges.length])

  const close = () => {
    trackHelpMePick('helpMePick:close', { ...progressRef.current })
    onClose()
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        trackHelpMePick('helpMePick:close', { ...progressRef.current })
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const matches = useMemo(() => filterMatches(challenges, answers), [challenges, answers])
  const preferredWeapon =
    answers.tool === 'SQL' || answers.tool === 'Python'
      ? (answers.tool as ChallengeService.TaskWeaponsOfChoiceEnum)
      : undefined

  if (!open) return null

  const start = (c: ChallengeService.AvailableChallenge) => {
    trackHelpMePick('helpMePick:start', {
      challengeId: challengeId(c),
      intensity: answers.intensity,
      tool: answers.tool,
      proficiencyCount: answers.proficiencies.length,
    })
    startChallenge(c, preferredWeapon !== undefined ? { preferredWeapon } : undefined)
  }

  const pickForMe = (source: ResultSource, pool = matches) => {
    if (pool.length === 0) return
    const { picked, nextServed } = pickUnserved(pool, readServed())
    writeServed(nextServed)
    trackHelpMePick('helpMePick:resultShown', { challengeId: challengeId(picked), source })
    setResult(picked)
    setScreen('result')
  }

  // Single-select answers select and advance in one click.
  const chooseIntensity = (v: Intensity) => {
    trackHelpMePick('helpMePick:answer:intensity', { intensity: v })
    setAnswers((a) => ({ ...a, intensity: v }))
    setStep(1)
  }
  const chooseTool = (v: ToolChoice) => {
    trackHelpMePick('helpMePick:answer:tool', { tool: v })
    setAnswers((a) => ({ ...a, tool: v }))
    setStep(2)
  }

  // Only the multi-select proficiency step uses an explicit Next.
  const finishProficiencies = () => {
    const matchCount = matches.length
    trackHelpMePick('helpMePick:answer:proficiencies', {
      count: answers.proficiencies.length,
      proficiencies: answers.proficiencies,
    })
    trackHelpMePick('helpMePick:results', {
      matchCount,
      outcome: matchCount === 0 ? 'none' : matchCount === 1 ? 'single' : 'multiple',
    })
    if (matchCount === 1) {
      trackHelpMePick('helpMePick:resultShown', { challengeId: challengeId(matches[0]), source: 'autoSingle' })
      setResult(matches[0])
      setCameFromMatches(false)
      setScreen('result')
      return
    }
    setStep(3)
  }

  const goPrev = () => {
    if (screen === 'result') {
      setScreen(cameFromMatches ? 'matches' : 'steps')
      return
    }
    if (screen === 'matches') {
      setScreen('steps')
      return
    }
    if (step > 0) setStep(step - 1)
  }

  const showBack = (screen === 'steps' && step > 0) || screen === 'matches' || screen === 'result'

  const intensityOptions: ChoiceOption<Intensity>[] = [
    { value: 'light', title: t('challenges.helpMePick.q1LightTitle'), icon: <Feather className="h-8 w-8" /> },
    { value: 'deep', title: t('challenges.helpMePick.q1DeepTitle'), icon: <Flame className="h-8 w-8" /> },
  ]
  const toolOptions: ChoiceOption<ToolChoice>[] = [
    {
      value: 'SQL',
      title: t('challenges.helpMePick.q2Sql'),
      icon: <WeaponIcon weapon={ChallengeService.TaskWeaponsOfChoiceEnum.Sql} className="h-8 w-8" />,
    },
    {
      value: 'Python',
      title: t('challenges.helpMePick.q2Python'),
      icon: <WeaponIcon weapon={ChallengeService.TaskWeaponsOfChoiceEnum.Python} className="h-8 w-8" />,
    },
    { value: 'either', title: t('challenges.helpMePick.q2Either'), icon: <Shuffle className="h-8 w-8" /> },
  ]

  const toggleProf = (p: TaskService.TaskProficienciesEnum) =>
    setAnswers((a) => ({
      ...a,
      proficiencies: a.proficiencies.includes(p)
        ? a.proficiencies.filter((x) => x !== p)
        : [...a.proficiencies, p],
    }))

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-sm transition-opacity duration-300 ${
        mounted ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {showBack && (
        <button
          type="button"
          onClick={goPrev}
          aria-label={t('challenges.helpMePick.previous')}
          className="absolute start-5 top-5 z-10 grid h-10 w-10 place-items-center rounded-full text-muted-foreground hover:bg-muted/40 hover:text-foreground"
        >
          <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
      )}
      <button
        type="button"
        onClick={close}
        aria-label={t('challenges.helpMePick.close')}
        className="absolute end-5 top-5 z-10 grid h-10 w-10 place-items-center rounded-full text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-6 py-6 sm:py-12">
        <h1 className="mb-4 flex-none text-center font-fraunces text-3xl font-light tracking-tight text-foreground sm:mb-8">
          {t('challenges.helpMePick.title')}
        </h1>

        {screen === 'steps' && (
          <>
            <MilestoneProgress count={STEP_COUNT} current={step} />
            {/* Full-bleed divider: -mx-6 cancels the container's px-6 padding. */}
            <div className="-mx-6 mt-8 h-px self-stretch bg-border" />
            <div className="flex w-full max-w-2xl flex-1 flex-col items-center">
              {/* Spacers in a 1:2 ratio sit the question a third down from the top. */}
              <div aria-hidden className="flex-[1]" />
              {step === 0 && (
                <>
                  <p className="mb-8 text-center text-2xl font-medium text-foreground">
                    {t('challenges.helpMePick.q1Title')}
                  </p>
                  <ChoiceCardGroup options={intensityOptions} value={answers.intensity} onChange={chooseIntensity} />
                </>
              )}
              {step === 1 && (
                <>
                  <p className="mb-8 text-center text-2xl font-medium text-foreground">
                    {t('challenges.helpMePick.q2Title')}
                  </p>
                  <ChoiceCardGroup options={toolOptions} value={answers.tool} onChange={chooseTool} />
                </>
              )}
              {step === 2 && (
                <>
                  <p className="mb-2 text-center text-2xl font-medium text-foreground">
                    {t('challenges.helpMePick.q3Title')}
                  </p>
                  <p className="mb-8 text-center text-sm text-muted-foreground">
                    {t('challenges.helpMePick.q3Subtitle')}
                  </p>
                  <ProficiencyPicker value={answers.proficiencies} onToggle={toggleProf} />
                  <Button size="lg" className="mt-10" onClick={finishProficiencies}>
                    {t('challenges.helpMePick.next')}
                  </Button>
                </>
              )}
              {step === 3 && (
                <div className="flex flex-col items-center">
                  {matches.length === 0 ? (
                    <>
                      <p className="mb-2 text-center text-2xl font-medium text-foreground">
                        {t('challenges.helpMePick.noMatchesTitle')}
                      </p>
                      <p className="mb-8 text-center text-sm text-muted-foreground">
                        {t('challenges.helpMePick.noMatchesSubtitle')}
                      </p>
                      <Button variant="outline" onClick={goPrev}>
                        {t('challenges.helpMePick.back')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="mb-2 text-center text-2xl font-medium text-foreground">
                        {t('challenges.helpMePick.finishTitle')}
                      </p>
                      <p className="mb-8 text-center text-sm text-muted-foreground">
                        {t('challenges.helpMePick.finishSubtitle', { count: matches.length })}
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-4">
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={() => {
                            trackHelpMePick('helpMePick:showAll', { matchCount: matches.length })
                            setCameFromMatches(false)
                            setScreen('matches')
                          }}
                        >
                          {t('challenges.helpMePick.showAll')}
                        </Button>
                        <Button
                          size="lg"
                          onClick={() => {
                            setCameFromMatches(false)
                            pickForMe('pickForMe')
                          }}
                        >
                          {t('challenges.helpMePick.pickForMe')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div aria-hidden className="flex-[2]" />
            </div>
          </>
        )}

        {screen === 'matches' && (
          <div className="mt-4 w-full max-w-5xl">
            <HelpMePickMatches
              challenges={matches}
              onPick={(c) => {
                trackHelpMePick('helpMePick:resultShown', { challengeId: challengeId(c), source: 'matchesList' })
                setResult(c)
                setCameFromMatches(true)
                setScreen('result')
              }}
            />
          </div>
        )}

        {screen === 'result' && result !== null && (
          <div className="mt-4 flex w-full min-h-0 flex-1 justify-center">
            <HelpMePickResult
              challenge={result}
              onStart={() => start(result)}
              onPickAnother={() => {
                trackHelpMePick('helpMePick:pickAnother')
                pickForMe('pickAnother')
              }}
              onBackToMatches={
                cameFromMatches
                  ? () => {
                      trackHelpMePick('helpMePick:backToMatches')
                      setScreen('matches')
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}
